import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { getErrorDetail } from "components/utility/utils";
import { fetchOrchidAPI } from "utils/api";
import buildUrlWithParams from "./buildUrlWithParams";
import { appointmentsFromApiData } from "contexts/appointments/utils";

const PAGE_SIZE = 100;

const select = (data) =>
  ({
    ...data,
    appointments: appointmentsFromApiData(data["appointments"] ?? []),
  } as { appointments: AppointmentMetaData[] });

const appointmentsV2QueryOptions = (params: {
  skip?: number;
  limit?: number;
  newestDatetime?: string;
  oldestDatetime?: string;
  patientUserId?: string;
  orchidProMayHaveMissedAppointment?: boolean;
  appointmentIsCancelledOrAbandoned?: boolean;
}) => {
  return queryOptions({
    queryKey: ["appointments-v2", params],
    queryFn: async ({ signal }) => {
      const url = buildUrlWithParams(
        "/api/scheduler/v1/snapshot/appointments_v2",
        params,
      );

      const res = await fetchOrchidAPI(url, { signal });
      if (!res.ok) {
        throw new Error(
          `failed to query appointments: ${getErrorDetail(await res.text())}`,
        );
      }
      const json = await res.json();
      return json;
    },
    select,
  });
};

export const appointmentsV2InfiniteQueryOptions = (params: {
  newestDatetime?: string;
  oldestDatetime?: string;
  patientUserId?: string;
  orchidProMayHaveMissedAppointment?: boolean;
  appointmentIsCancelledOrAbandoned?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: ["appointments-v2", params],
    queryFn: async ({ pageParam, signal }) => {
      const url = buildUrlWithParams(
        "/api/scheduler/v1/snapshot/appointments_v2",
        {
          ...params,
          limit: PAGE_SIZE,
          skip: pageParam * PAGE_SIZE,
        },
      );

      const res = await fetchOrchidAPI(url, { signal });
      if (!res.ok) {
        throw new Error(
          `failed to list patient chart: ${getErrorDetail(await res.text())}`,
        );
      }
      const json = await res.json();
      return json;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const hasMore = pages.length * PAGE_SIZE < lastPage.total_count;
      if (!hasMore) {
        return undefined;
      }
      return pages.length;
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map(select),
    }),
  });
};

export default appointmentsV2QueryOptions;
